/**
 * Memory Operation Card
 *
 * Renders a highlighted card when agent invokes append/replace/delete memory ops.
 * Color-coded by operation type.
 * "View in Memory Panel" button navigates to agent's memory page.
 */

import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/shared/components/ui/button'
import { useSessionStore } from '../store'
import type { MemoryOp } from '../types'

const MEMORY_OP_STYLES: Record<MemoryOp['op'], {
  border: string
  bg: string
  headerBg: string
  headerText: string
  textColor: string
}> = {
  append: {
    border: 'rgba(134, 239, 172, 0.5)',
    bg: 'rgba(134, 239, 172, 0.1)',
    headerBg: 'rgba(134, 239, 172, 0.2)',
    headerText: 'rgb(22, 101, 52)',
    textColor: 'rgb(22, 101, 52)',
  },
  replace: {
    border: 'rgba(147, 197, 253, 0.5)',
    bg: 'rgba(147, 197, 253, 0.1)',
    headerBg: 'rgba(147, 197, 253, 0.2)',
    headerText: 'rgb(30, 64, 175)',
    textColor: 'rgb(30, 64, 175)',
  },
  delete: {
    border: 'rgba(252, 165, 165, 0.5)',
    bg: 'rgba(252, 165, 165, 0.1)',
    headerBg: 'rgba(252, 165, 165, 0.2)',
    headerText: 'rgb(153, 27, 27)',
    textColor: 'rgb(153, 27, 27)',
  },
}

const MEMORY_OP_HEADERS = {
  append: 'MEMORY ADDED',
  replace: 'MEMORY REPLACED',
  delete: 'MEMORY DELETED',
}

export function MemoryOpCard({ memOp }: { memOp: MemoryOp }) {
  const navigate = useNavigate()
  const currentAgentId = useSessionStore((state) => state.currentAgentId)
  const opStyles = MEMORY_OP_STYLES[memOp.op]
  const header = MEMORY_OP_HEADERS[memOp.op]

  const handleViewInMemory = () => {
    if (currentAgentId) {
      navigate(`/memory/${currentAgentId}`)
    }
  }

  return (
    <div
      style={{
        ...styles.container,
        borderColor: opStyles.border,
        background: opStyles.bg,
      }}
      data-testid={`memory-op-card-${memOp.id}`}
    >
      <div
        style={{
          ...styles.header,
          background: opStyles.headerBg,
          color: opStyles.headerText,
        }}
      >
        {header}
      </div>
      <div style={{ ...styles.content, color: opStyles.textColor }}>
        <p>
          <span style={styles.label}>Category:</span> {memOp.category}
        </p>
        <p>
          <span style={styles.label}>Fact:</span> {memOp.fact}
        </p>
      </div>
      <div style={styles.footer}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleViewInMemory}
          style={styles.button}
          data-testid={`view-memory-button-${memOp.id}`}
        >
          View in Memory Panel
        </Button>
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    borderRadius: 'var(--radius-sm)',
    border: '1px solid',
  },
  header: {
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 600,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px 12px',
    fontSize: '12px',
  },
  label: {
    fontWeight: 600,
  },
  footer: {
    borderTop: '1px solid rgba(128, 128, 128, 0.2)',
    padding: '8px 12px',
  },
  button: {
    width: '100%',
    fontSize: '12px',
  },
}
